/**
 * A `{{token}}` template, read as text: the pure half of keepr's two pill
 * editors. TokenInput (the hand-built contentEditable the template surfaces
 * use) and FormulaInput (the Lexical formula editor, KPR-539) both parse,
 * label and resolve references through these, so a reference means the same
 * thing in either. Nothing here touches the DOM.
 *
 * Moved out of TokenInput.js unchanged when the formula editor moved to
 * Lexical; TokenInput re-exports every name, so existing imports still work.
 */

// `{{...}}` with no nested braces. Deliberately permissive about what is inside:
// the token vocabulary is the API's business, and an unrecognized token must
// round-trip untouched rather than be silently dropped.
export const TOKEN_RE = /\{\{[^{}]*\}\}/g;

// ------------------------------------------------------------ token vocabulary

/** Every node of a token tree that carries a token, in menu order. */
export function flattenTokenNodes(nodes) {
	const out = [];
	const walk = list => (Array.isArray(list) ? list : []).forEach(node => {
		if (!node) return;
		if (node.token) out.push(node);
		if (Array.isArray(node.children)) walk(node.children);
	});
	walk(nodes);
	return out;
}

// Every tier of the token menu is marked, so a surface with several dropdowns
// on screen (the rule editor's "Add action" menu, say) can still address THIS
// one — submenu popups are separate portals and inherit nothing from the root.
export const TOKEN_MENU_CLASS = 'token-input__menu';
export const TOKEN_SUBMENU_CLASS = 'token-input__submenu';

/**
 * Token tree -> antd Menu items, plus the key -> token map clicks resolve
 * against.
 *
 * Keys are POSITIONAL (`t-0-2`), not the token text. The same token appears in
 * more than one place on purpose — `{{today}}` is both the "Today" group's own
 * value and its first child, and a card-lookup is repeated inside its own
 * submenu — and rc-menu needs every key in the tree to be unique or it renders
 * one of the duplicates inert.
 */
export function buildTokenMenu(nodes) {
	const tokenByKey = new Map();
	const build = (list, trail) => (Array.isArray(list) ? list : [])
		.filter(Boolean)
		.map((node, index) => {
			const key = `${trail}-${index}`;
			const item = { key, label: node.menuLabel || node.label || node.token };
			if (node.disabled) item.disabled = true;
			else if (node.token) tokenByKey.set(key, node.token);
			if (Array.isArray(node.children) && node.children.length) {
				item.children = build(node.children, key);
				item.popupClassName = TOKEN_SUBMENU_CLASS;
			}
			return item;
		});
	return { items: build(nodes, 't'), tokenByKey };
}

/**
 * Template string -> segments. `[{type:'text', text}]` and
 * `[{type:'token', token:'{{today}}', inner:'today'}]`, in document order.
 * Text is preserved verbatim, whitespace included. In a formula (`formula`),
 * a `{{…}}` inside a text literal (formulaLiteralSpans) is text, so it is
 * drawn as text, never as a pill (the charts release 3 review's m1).
 */
export function parseTemplate(value, { formula = false } = {}) {
	const text = typeof value === 'string' ? value : '';
	const literal = formula && text.includes('{{') ? formulaLiteralSpans(text) : [];
	const inLiteral = at => literal.some(([start, end]) => at >= start && at < end);
	const segments = [];
	let pending = '';
	const flush = () => {
		if (pending) segments.push({ type: 'text', text: pending });
		pending = '';
	};
	let last = 0;
	let match;
	TOKEN_RE.lastIndex = 0;
	while ((match = TOKEN_RE.exec(text)) !== null) {
		pending += text.slice(last, match.index);
		if (inLiteral(match.index)) pending += match[0];
		else {
			flush();
			segments.push({ type: 'token', token: match[0], inner: match[0].slice(2, -2).trim() });
		}
		last = match.index + match[0].length;
	}
	pending += text.slice(last);
	flush();
	return segments;
}

/** Segments -> template string. `serialize(parse(x)) === x` for every string. */
export function serializeSegments(segments) {
	return (segments || [])
		.map(segment => (segment.type === 'token' ? segment.token : segment.text))
		.join('');
}

// A formula's `{{…}}` reference, as the formula module reads one (its REF_RE).
const FORMULA_REF_RE = /^\{\{[^{}]*\}\}/;

/**
 * The [start, end) spans of a formula's text literals, read as the formula
 * module's tokenizer reads them (utils/formula: a reference first, then "…" or
 * '…' with \\ and \" escapes; an unclosed quote runs to the end). A `{{…}}`
 * inside one is text, not a reference.
 */
export function formulaLiteralSpans(text) {
	const spans = [];
	let i = 0;
	while (i < text.length) {
		const ref = text.startsWith('{{', i) ? FORMULA_REF_RE.exec(text.slice(i)) : null;
		if (ref) { i += ref[0].length; continue; }
		const quote = text[i];
		if (quote !== '"' && quote !== '\'') { i += 1; continue; }
		let j = i + 1;
		while (j < text.length) {
			if (text[j] === '\\' && j + 1 < text.length && (text[j + 1] === '\\' || text[j + 1] === quote)) { j += 2; continue; }
			if (text[j] === quote) { j += 1; break; }
			j += 1;
		}
		spans.push([i, Math.min(j, text.length)]);
		i = j;
	}
	return spans;
}

/**
 * A template with each `{{Label}}` written by a person turned into the token
 * that label names: only where the text is not a token of the vocabulary
 * already, and the label names exactly one token (`tokenByLabel` holds null
 * for a label two tokens share). In a formula (`formula`), a `{{…}}` inside a
 * text literal is left alone: it is text there, and must stay as typed.
 *
 * Returns `{ text, edits }`: `edits` are `[start, end, length]` in the
 * template given, so a caret can be carried across the change (`shiftOffset`).
 */
export function resolveLabelTokens(template, knownTokens, tokenByLabel, { formula = false } = {}) {
	const edits = [];
	if (typeof template !== 'string' || !template.includes('{{')) return { text: template, edits };
	const literal = formula ? formulaLiteralSpans(template) : [];
	const inLiteral = at => literal.some(([start, end]) => at >= start && at < end);
	const text = template.replace(TOKEN_RE, (whole, at) => {
		if (knownTokens.has(whole) || inLiteral(at)) return whole;
		const token = tokenByLabel.get(whole.slice(2, -2).trim());
		if (!token) return whole;
		edits.push([at, at + whole.length, token.length]);
		return token;
	});
	return { text, edits };
}

/** An offset in a template, carried across `edits`: past a changed reference it lands just after it. */
export function shiftOffset(offset, edits) {
	let shift = 0;
	for (const [start, end, length] of edits) {
		if (offset >= end) shift += length - (end - start);
		else if (offset > start) return start + shift + length;
	}
	return offset + shift;
}

/** Do two segment lists mean the same template? Drives the "repaint?" decision. */
export function segmentsEqual(a, b) {
	if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
	return a.every((segment, i) => {
		const other = b[i];
		if (segment.type !== other.type) return false;
		return segment.type === 'token' ? segment.token === other.token : segment.text === other.text;
	});
}
